import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  AppBar,
  Toolbar,
  Button,
  Drawer,
  IconButton,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { ChatMessage } from '../components/ChatMessage';
import { ChatInput } from '../components/ChatInput';
import { SessionList } from '../components/SessionList';
import { UserSelector } from '../components/UserSelector';
import { QueryProgressIndicator } from '../components/QueryProgressIndicator';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import type { Message, Conversation, ChatResponse } from '../types';

const DRAWER_WIDTH = 320;
const CONVERSATION_STORAGE_KEY = 'holmesglen_active_conversation';
const MAX_RETRY_ATTEMPTS = 3;  // Increased to handle cold start scenarios
const RETRY_DELAY_MS = 1000;   // 1 second base delay for more reliable loading
const INITIAL_LOAD_DELAY_MS = 500; // Small delay before first load to allow backend warmup

// Helper functions for localStorage persistence
const getStoredConversationId = (userId: string): string | null => {
  try {
    const stored = localStorage.getItem(CONVERSATION_STORAGE_KEY);
    if (stored) {
      const data = JSON.parse(stored);
      // Only return if it belongs to the current user
      if (data.userId === userId) {
        return data.conversationId;
      }
    }
  } catch (e) {
    console.warn('Failed to read stored conversation:', e);
  }
  return null;
};

const storeConversationId = (userId: string, conversationId: string | null) => {
  try {
    if (conversationId) {
      localStorage.setItem(CONVERSATION_STORAGE_KEY, JSON.stringify({
        userId,
        conversationId,
        timestamp: Date.now(),
      }));
    } else {
      localStorage.removeItem(CONVERSATION_STORAGE_KEY);
    }
  } catch (e) {
    console.warn('Failed to store conversation:', e);
  }
};

// Helper function to delay execution
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const Chat: React.FC = () => {
  const { isAuthenticated, userId, userEmail, logout } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [conversationsLoaded, setConversationsLoaded] = useState(false);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const loadAttemptRef = useRef(0); // Track load attempts to prevent duplicate concurrent loads

  const enableAuth = import.meta.env.VITE_ENABLE_AUTH === 'true';

  // Load conversations on mount and when userId changes
  useEffect(() => {
    if (userId && isAuthenticated) {
      // Only reset state if user actually changed (not on refresh)
      const storedId = getStoredConversationId(userId);
      if (!storedId) {
        // No stored conversation for this user, reset state
        setMessages([]);
        setActiveConversationId(null);
      }
      setConversationsLoaded(false);
      setError(null);

      // Increment load attempt to track this specific load request
      const currentAttempt = ++loadAttemptRef.current;

      // Load conversations for the user with a small initial delay
      // This allows the backend warmup trigger time to initialize
      const loadWithDelay = async () => {
        await delay(INITIAL_LOAD_DELAY_MS);
        // Only proceed if this is still the most recent load request
        if (loadAttemptRef.current === currentAttempt) {
          loadConversations();
        }
      };
      loadWithDelay();
    }
  }, [userId, isAuthenticated]);

  // Restore conversation from localStorage after conversations are loaded
  useEffect(() => {
    if (conversationsLoaded && userId && !activeConversationId) {
      const storedId = getStoredConversationId(userId);
      if (storedId) {
        // Verify the conversation still exists
        const exists = conversations.some(c => c.id === storedId);
        if (exists) {
          console.log('Restoring conversation from localStorage:', storedId);
          setActiveConversationId(storedId);
        } else {
          // Conversation no longer exists, clear storage
          console.log('Stored conversation no longer exists, clearing');
          storeConversationId(userId, null);
        }
      }
    }
  }, [conversationsLoaded, conversations, userId]);

  // Persist active conversation to localStorage
  useEffect(() => {
    if (userId && conversationsLoaded) {
      storeConversationId(userId, activeConversationId);
    }
  }, [activeConversationId, userId, conversationsLoaded]);

  // Load messages when active conversation changes or conversations are updated
  useEffect(() => {
    if (activeConversationId && conversations.length > 0) {
      loadConversationMessages(activeConversationId);
    } else if (!activeConversationId) {
      setMessages([]);
    }
  }, [activeConversationId, conversations]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversations = async (retryCount = 0): Promise<void> => {
    if (!userId) {
      console.log('No userId, skipping conversation load');
      return;
    }

    // Prevent concurrent loads
    if (conversationsLoading && retryCount === 0) {
      console.log('Already loading conversations, skipping duplicate request');
      return;
    }

    setConversationsLoading(true);
    console.log(`Loading conversations for user: ${userId} (attempt ${retryCount + 1}/${MAX_RETRY_ATTEMPTS})`);

    try {
      const convs = await apiService.getUserConversations(userId);
      console.log('Loaded conversations:', convs.length);
      setConversations(convs);
      setConversationsLoaded(true);
      setConversationsLoading(false);
    } catch (err: any) {
      console.error('Failed to load conversations:', err);

      // Check if this is a network/timeout error that warrants retry
      const isRetryableError = err?.code === 'ERR_NETWORK' ||
                               err?.code === 'ECONNABORTED' ||
                               err?.message?.includes('timeout') ||
                               err?.message?.includes('Network Error') ||
                               err?.response?.status >= 500;

      // Retry on failure (up to MAX_RETRY_ATTEMPTS)
      if (retryCount < MAX_RETRY_ATTEMPTS - 1 && isRetryableError) {
        const delayMs = RETRY_DELAY_MS * Math.pow(1.5, retryCount); // Exponential backoff
        console.log(`Retrying in ${delayMs}ms... (attempt ${retryCount + 2}/${MAX_RETRY_ATTEMPTS})`);
        await delay(delayMs);
        return loadConversations(retryCount + 1);
      }

      // After all retries failed or non-retryable error
      setConversationsLoading(false);

      // Only set conversationsLoaded if we have no existing conversations
      // This prevents clearing conversations that were already loaded
      if (conversations.length === 0) {
        setConversationsLoaded(true);
      }
      // Don't clear existing conversations on error - keep what we have
      console.warn(`All ${retryCount + 1} retry attempts failed, keeping existing ${conversations.length} conversations`);
    }
  };

  const loadConversationMessages = (conversationId: string) => {
    const conversation = conversations.find(c => c.id === conversationId);
    if (!conversation) return;

    const msgs: Message[] = [];
    conversation.conversations.forEach((conv) => {
      msgs.push({
        id: `${conv.id}-user`,
        role: 'user',
        content: conv.content.user,
        timestamp: conv.timestamp,
      });
      msgs.push({
        id: conv.id,
        role: 'assistant',
        content: conv.content.assistant,
        timestamp: conv.timestamp,
        metadata: conv.metadata,
      });
    });
    setMessages(msgs);
  };

  const handleSendMessage = async (messageText: string) => {
    if (!messageText.trim()) return;

    console.log('handleSendMessage - Setting loading to TRUE');
    setLoading(true);
    setError(null);

    // Add user message to UI immediately
    const userMessage: Message = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: messageText,
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMessage]);

    try {
      const response: ChatResponse = await apiService.sendMessage({
        user_query: messageText,
        user_id: userId,
        user_email: userEmail,
        conversation_id: activeConversationId || undefined,
        user_names: [], // Empty array means search all documents regardless of owner
        // num_chunks removed - uses backend DEFAULT_NUM_CHUNKS env var
        revise_chunks: false,
        revise_citations: false,
      });

      // Debug: Log the response to see what chunks we received
      console.log('Chat Response:', response);
      console.log('Chunks from backend:', response.results.chunks);
      console.log('Number of chunks:', response.results.chunks?.length);

      // Pass raw chunks — ChatMessage handles deduplication by file_name with page range merging
      const deduplicatedChunks = response.results.chunks || [];

      // Add assistant response
      const assistantMessage: Message = {
        id: response.results.message_id,
        role: 'assistant',
        content: response.results.response,
        timestamp: new Date().toISOString(),
        metadata: {
          chunks: deduplicatedChunks,
        },
      };
      setMessages(prev => [...prev, assistantMessage]);

      // Update conversation ID if this was a new conversation
      if (!activeConversationId && response.results.conversation_id) {
        setActiveConversationId(response.results.conversation_id);
        // Reload conversations to show the new one
        await loadConversations();
      }
    } catch (err: any) {
      console.error('Failed to send message:', err);
      const errorMessage = err?.error?.message || 'Failed to send message';
      setError(errorMessage);

      // Remove the optimistically added user message on error
      setMessages(prev => prev.filter(m => m.id !== userMessage.id));
    } finally {
      console.log('handleSendMessage - Setting loading to FALSE');
      setLoading(false);
    }
  };

  const handleNewConversation = () => {
    setActiveConversationId(null);
    setMessages([]);
  };

  const handleSelectConversation = (id: string) => {
    setActiveConversationId(id);
  };

  if (!isAuthenticated) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
        }}
      >
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setDrawerOpen(!drawerOpen)}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>

          <Box
            component="img"
            src="/logo.svg"
            alt="Holmesglen"
            sx={{
              height: 32,
              mr: 2,
              filter: 'brightness(0) invert(1)', // Make logo white for dark header
            }}
          />
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            PPL AI Assistant
          </Typography>

          {enableAuth && (
            <Button color="inherit" startIcon={<LogoutIcon />} onClick={logout}>
              Logout
            </Button>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        variant="persistent"
        open={drawerOpen}
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
          },
        }}
      >
        <Toolbar />
        {!enableAuth && <UserSelector />}
        <SessionList
          conversations={conversations}
          activeConversationId={activeConversationId}
          onSelectConversation={handleSelectConversation}
          onNewConversation={handleNewConversation}
          isLoading={conversationsLoading}
        />
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          marginLeft: drawerOpen ? 0 : `-${DRAWER_WIDTH}px`,
          transition: 'margin-left 0.3s',
        }}
      >
        <Toolbar />

        <Box
          sx={{
            flexGrow: 1,
            overflow: 'auto',
            p: 3,
            bgcolor: 'background.default',
          }}
        >
          <Container maxWidth="lg">
            {messages.length === 0 && !loading ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  minHeight: 400,
                }}
              >
                <Box
                  component="img"
                  src="/logo.svg"
                  alt="Holmesglen"
                  sx={{
                    height: 80,
                    mb: 3,
                    opacity: 0.5,
                  }}
                />
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  Welcome to Policy and Procedure Library AI Assistant
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Ask me anything about Holmesglen policies, procedures, and documents
                </Typography>
              </Box>
            ) : (
              <>
                {messages.map((message) => (
                  <ChatMessage key={message.id} message={message} />
                ))}
                <QueryProgressIndicator isLoading={loading} />
                <div ref={messagesEndRef} />
              </>
            )}
          </Container>
        </Box>

        <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
          <Container maxWidth="lg">
            <ChatInput onSend={handleSendMessage} loading={loading} />
          </Container>
        </Box>
      </Box>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};
